import React from "react";
import {
  X, User, FileText, CheckCircle,
  XCircle, Clock, ExternalLink, Building, GraduationCap,
} from "lucide-react";
import { FORM_GRADE_OPTIONS, PATHWAY_OPTIONS, getOptionLabel } from "../utils/academicLevels";

const formatKES = (amt) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency", currency: "KES", maximumFractionDigits: 0,
  }).format(amt || 0);

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-KE", { dateStyle: "long" }) : "Not available";

const STATUS_CONFIG = {
  DRAFT:        { color: "bg-gray-100 text-gray-600 border-gray-200",    icon: FileText,    label: "Draft" },
  SUBMITTED:    { color: "bg-blue-100 text-blue-700 border-blue-200",    icon: Clock,       label: "Submitted" },
  UNDER_REVIEW: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock,    label: "Under Review" },
  APPROVED:     { color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle, label: "Approved" },
  REJECTED:     { color: "bg-red-100 text-red-700 border-red-200",       icon: XCircle,     label: "Rejected" },
};

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
        <Icon size={14} className="text-primary" />
      </div>
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
    </div>
    <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100">
      {children}
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between items-start px-4 py-2.5 gap-3">
    <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-800 text-right break-all">{value || "Not available"}</span>
  </div>
);

const DocLink = ({ label, url }) => {
  if (!url) return (
    <div className="flex justify-between items-center px-4 py-2.5">
      <span className="text-xs text-gray-400">{label}</span>
      <span className="text-xs text-gray-300 italic">Not uploaded</span>
    </div>
  );
  return (
    <div className="flex justify-between items-center px-4 py-2.5">
      <span className="text-xs text-gray-400">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs font-medium text-primary hover:text-blue-800 transition"
      >
        View <ExternalLink size={11} />
      </a>
    </div>
  );
};

const LEVEL_LABELS = {
  SECONDARY: "Secondary School",
  COLLEGE: "College",
  UNIVERSITY: "University",
};

const ApplicationModal = ({ app, onClose }) => {
  if (!app) return null;

  const student      = app.studentId || {};
  const institution   = student.institution || {};
  const progress      = app.academicProgress || {};
  const ward          = student.ward;
  const wardName      = typeof ward === "object" ? ward?.name : ward;
  const cfg           = STATUS_CONFIG[app.status] || STATUS_CONFIG.DRAFT;
  const Icon          = cfg.icon;
  const isSecondary   = student.schoolLevel === "SECONDARY";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col border border-gray-100">

        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
              {student.fullName?.charAt(0)?.toUpperCase() || "S"}
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base">{student.fullName || "Unknown Student"}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.color}`}>
                  <Icon size={10} /> {cfg.label}
                </span>
                <span className="text-xs text-gray-400">{app.academicYear}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5 flex-1">

          <Section title="Student Information" icon={User}>
            <Row label="Full Name"    value={student.fullName} />
            <Row label="School Level" value={LEVEL_LABELS[student.schoolLevel] || student.schoolLevel} />
            <Row label="Ward"         value={wardName} />
            <Row label="Village"      value={student.village} />
          </Section>

          <Section title="Institution Details" icon={Building}>
            <Row label="Institution" value={institution.institutionName} />
            {isSecondary ? (
              <Row label="Admission No." value={institution.admissionNumber} />
            ) : (
              <>
                <Row label="Course" value={institution.course} />
                {student.schoolLevel === "COLLEGE" && (
                  <Row label="Course Level" value={institution.courseLevel} />
                )}
                <Row label="Registration No." value={institution.registrationNumber} />
              </>
            )}
          </Section>

          <Section title="This Year's Progress" icon={GraduationCap}>
            {isSecondary ? (
              <>
                <Row label="Grade / Form" value={progress.formGrade ? getOptionLabel(FORM_GRADE_OPTIONS, progress.formGrade) : null} />
                {progress.pathway && <Row label="Pathway" value={getOptionLabel(PATHWAY_OPTIONS, progress.pathway)} />}
                {progress.stream && <Row label="Class / Stream" value={progress.stream} />}
              </>
            ) : (
              <Row label="Year of Study" value={progress.yearOfStudy} />
            )}
            <Row label="Total Fees"  value={formatKES(app.fees?.totalFees)} />
            <Row label="Fee Balance" value={formatKES(app.fees?.feeBalance)} />
          </Section>

          <Section title="Timeline" icon={Clock}>
            <Row label="Submitted"    value={formatDate(app.submittedAt)} />
            <Row label="Reviewed By"  value={app.reviewedBy?.name} />
            <Row label="Last Updated" value={formatDate(app.updatedAt)} />
          </Section>

          {app.remarks && (
            <div className={`p-4 rounded-xl border-l-4 text-sm
              ${app.status === "REJECTED"
                ? "bg-red-50 border-red-400 text-red-700"
                : "bg-blue-50 border-primary text-blue-700"}`}
            >
              <p className="font-semibold mb-1">
                {app.status === "REJECTED" ? "Rejection Reason" : "Remarks"}
              </p>
              <p>{app.remarks}</p>
            </div>
          )}

          <Section title="Uploaded Documents" icon={FileText}>
            <DocLink label="ID / Birth Certificate" url={app.documents?.studentIdDoc} />
            <DocLink label="Fee Structure"          url={app.documents?.feeStructure} />
            <DocLink label="Admission Letter"       url={app.documents?.admissionLetter} />
            <DocLink label="Guardian ID"            url={app.documents?.guardianIdDoc} />
            <DocLink label="Report Card"            url={app.documents?.reportCard} />
            <DocLink label="Orphan Proof"           url={app.documents?.orphanProof} />
            <DocLink label="Disability Proof"       url={app.documents?.disabilityProof} />
          </Section>

        </div>

        <div className="flex justify-end p-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;